/**
 * Phase0Page — page-level orchestrator for the P0 artifacts workspace. **Replaces `Phase0Panel`**
 * (875 lines, the largest single component in the tree) per CR §3.4.
 *
 * ── WHAT CHANGED STRUCTURALLY ──────────────────────────────────────────────────────────────────
 * `Phase0Panel` was a card containing an upload list, a generation-status block, five permanent
 * artifact tabs, and — unrelatedly — the whole `PhaseGovernanceSection`. This page keeps only the
 * first three concerns and inverts the layout:
 *
 *   • The **project chat is the default view**, shown whenever no file tab is open (CR §3.4).
 *   • Artifacts are **files you open**, via `FileTabStrip` + `SourcesSheet`, not five cards that are
 *     always mounted.
 *   • Sources moved from an inline section into a **non-modal** side panel that stays open while
 *     you work.
 *
 * `PhaseGovernanceSection` was extracted to its own module untouched. It lives on the project
 * Overview tab and has nothing to do with artifact editing; it was only ever in `Phase0Panel.tsx`
 * because that file grew. Co-locating it with a page that no longer exists would have made it
 * collateral damage of this change.
 *
 * ── GENERATION STATUS, AND WHY IT IS A BANNER NOW ──────────────────────────────────────────────
 * `Phase0Panel` gated the artifact tabs behind `generation?.status === 'complete'`. That coupling is
 * dropped deliberately: the chat is usable before generation (it answers questions), and a user
 * whose last job failed still needs access to the artifacts the previous successful run produced.
 * Status is surfaced as a banner instead of a gate, and the Generated-docs list in `SourcesSheet`
 * is what gates on `complete`.
 *
 * The 10-second poll while a job is `generating` is unchanged (FR-MVP-029). It polls
 * `['phase0-status']` — a different key from the chat transcript — so it does not touch the
 * no-polling rule Phase E established for `['project-chat']` (see useProjectChat.ts §E7).
 */

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Info, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useProject } from "@/hooks/useProjects";
import {
  canActAsMember,
  toastIfNotProjectMember,
} from "@/lib/project-permissions";
import {
  ARTIFACT_TYPES,
  useArtifact,
  type ArtifactsState,
  type ArtifactStatus,
  type ArtifactType,
} from "@/hooks/useArtifacts";
import { ConflictDialog } from "@/components/projects/artifacts/ConflictDialog";
import type { InputCustomerMismatchDetails, Project } from "@/types";
import type { DocsSyncStatus } from "@/lib/sharepoint-link";
import { CustomerMismatchDialog } from "./CustomerMismatchDialog";
import { FileContentView } from "./FileContentView";
import { FileTabStrip } from "./FileTabStrip";
import { SourcesSheet } from "./SourcesSheet";
import { ProjectChatView } from "./chat/ProjectChatView";
import { CHAT_TAB, useFileTabs, type FileTabId } from "./useFileTabs";

// ─── Types (mirror phase0_generations, arch §6.3) ──────────────────────────────
// Duplicated intentionally: the frontend is a standalone vite workspace with no dependency on
// packages/shared — same convention as UnifiedTeamSection / MeetingIntelligenceCard.

type Phase0JobStatus = "pending" | "generating" | "complete" | "failed";

interface Phase0Artifact {
  type: string;
  content: string;
}

interface Phase0Generation {
  id: string;
  project_id: string;
  status: Phase0JobStatus;
  triggered_at: string;
  completed_at: string | null;
  artifacts: Phase0Artifact[];
  error_message: string | null;
  /**
   * Filename promoted to `sow_content` by the §2.6 inference fallback, or null when there was
   * nothing to disclose. Delivered on THIS polling response rather than on the generate response
   * because generate blocks for ~660 s — see V042 and AD-4a.
   */
  sow_inferred_filename?: string | null;
}

/** POST /phase0/generate: 202 { id, status: 'pending' } (async hand-off), or 200 when run inline. */
interface GenerateResponse {
  id: string;
  status: Phase0JobStatus;
}

/** Placeholder generation used when the 202 arrives before the first status fetch. */
function emptyGeneration(projectId: string): Phase0Generation {
  return {
    id: "",
    project_id: projectId,
    status: "pending",
    triggered_at: new Date().toISOString(),
    completed_at: null,
    artifacts: [],
    error_message: null,
    sow_inferred_filename: null,
  };
}

interface Phase0StatusResponse {
  generation: Phase0Generation | null;
  uploaded_files: string[];
  /**
   * Newest input file's mtime, or null when there are no inputs. Drives `ChatSystemPrompt` state C
   * ("sources changed since the last generation"), which is the difference between offering
   * Regenerate and not. Additive — see §3.4.
   */
  latest_input_uploaded_at?: string | null;
  /**
   * Download state of the latest SharePoint link / Oscar sync into the Phase 0 inputs (POST
   * /phase0/link). null = no sync has run. While pending/syncing the status is polled faster
   * (PHASE0_LINK_POLL_MS) so the file appears and generation is noticed without a refresh.
   */
  docs_sync_status?: DocsSyncStatus | null;
  /** ISO timestamp of the last completed sync, or null. */
  docs_synced_at?: string | null;
}

export interface Phase0PageProps {
  projectId: string;
}

const WRITE_ROLES = ["pm", "sa", "admin", "leadership"];

/** Statuses that make an artifact unsafe to overwrite without an explicit decision (FR-MVP-026). */
const LOCKED_STATUSES: ArtifactStatus[] = ["pending_commit", "committed"];

/**
 * Fallback status poll while a generation job is in flight (FR-MVP-029). Updates normally arrive
 * over the realtime WebSocket (job_type `phase0_generation`, see useWebSocket.ts); this poll only
 * covers a socket that is offline or reconnecting.
 */
export const PHASE0_STATUS_POLL_MS = 30_000;

/**
 * Status poll while a SharePoint link download (POST /phase0/link) is pending or syncing. Faster
 * than the generation poll: the download takes seconds, and the server starts generation as soon
 * as the file lands, so the user should see the file (and the run) promptly.
 */
export const PHASE0_LINK_POLL_MS = 10_000;

/**
 * `refetchInterval` for `['phase0-status']`. Polls while the job is in flight — `'generating'` AND
 * `'pending'`: both generators sit in `'pending'` during extraction, and a stop (Stop button, or a
 * source file deleted) can land then, so the UI must notice it without a manual refresh.
 *
 * Also polls (at the faster PHASE0_LINK_POLL_MS) while a SharePoint link download is
 * `'pending'`/`'syncing'`. The second argument is optional, so one-argument callers are unchanged.
 */
export function phase0StatusRefetchInterval(
  status: string | null | undefined,
  docsSyncStatus?: string | null,
): number | false {
  if (docsSyncStatus === "pending" || docsSyncStatus === "syncing") {
    return PHASE0_LINK_POLL_MS;
  }
  return status === "generating" || status === "pending"
    ? PHASE0_STATUS_POLL_MS
    : false;
}

/** Options of one POST /phase0/generate. */
export interface GenerateOptions {
  /** Leadership: overwrite committed artifacts (ConflictDialog). */
  force?: boolean;
  /** T-40: proceed past INPUT_CUSTOMER_MISMATCH — audited server-side. */
  overrideCustomerCheck?: boolean;
}

/** Accepts the legacy boolean `force` argument as well as the options object. */
export function normaliseGenerateOptions(
  opts: boolean | GenerateOptions | undefined,
): GenerateOptions {
  if (typeof opts === "boolean") return { force: opts };
  return {
    force: opts?.force === true,
    overrideCustomerCheck: opts?.overrideCustomerCheck === true,
  };
}

function errorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as AxiosError<{ message?: string }>;
  return (
    axiosErr?.response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

function statusBadge(status: Phase0JobStatus): JSX.Element {
  switch (status) {
    case "complete":
      return <Badge variant="success">Complete</Badge>;
    case "generating":
      return <Badge variant="info">Generating…</Badge>;
    case "pending":
      return <Badge variant="neutral">Pending</Badge>;
    case "failed":
      return <Badge variant="danger">Failed</Badge>;
    default:
      return <Badge>{status}</Badge>;
  }
}

/**
 * Reads the five artifact statuses. **TWO consumers**, and the second is what makes the return type
 * load-bearing (2026-10-03 fix-batch design §3.3.1, AD-5b; C6 in the stale-comment manifest):
 *
 *   1. **Regeneration** — `hasLocked` decides whether the `ConflictDialog` is needed before a
 *      generate.
 *   2. **Artifact EXISTENCE** — `SourcesSheet`'s file list, `ChatSystemPrompt`'s state machine and
 *      `ArtifactTagBar`'s chip availability all derive "which artifacts exist" from `artifacts`,
 *      because `phase0_generations.status` answers a different question (§4.1).
 *
 * `artifacts` is LOAD-BEARING for consumer 2. `statuses` ALONE CANNOT TELL "no artifact" FROM
 * "DON'T KNOW YET" FROM "THE REQUEST FAILED" — all three write no key. A fully generated project
 * whose artifact queries are pending (cold load) or errored (5xx, throttle, token race) would
 * otherwise render the "not generated yet" prompt with a primary Generate button wired to the
 * NON-ConflictDialog mutation, and a click in that window overwrites live drafts. The errored case
 * does not self-resolve: `useArtifact` sets `retry: false`.
 *
 * `statuses` is complete ONLY in the `'ready'` phase. Consumer 1 is safe because every caller of
 * `handleRegenerateClick` is itself gated on `phase === 'ready'` — see the invariant note at the
 * `hasLocked` derivation below.
 *
 * Fixed-order hooks (rules-of-hooks safe — the artifact list is a stable constant), and the queries
 * dedupe with the ones `FileContentView` runs, so this costs no extra requests.
 */
function useArtifactStatuses(projectId: string): {
  statuses: Record<string, string>;
  artifacts: ArtifactsState;
} {
  const queryClient = useQueryClient();

  const queries = [
    useArtifact(projectId, "preliminary_srs"),
    useArtifact(projectId, "discovery_agenda"),
    useArtifact(projectId, "project_plan"),
    useArtifact(projectId, "baseline_jira_backlog"),
    useArtifact(projectId, "kickoff_deck"),
  ];

  // Prefix invalidate — `artifactQueryKey` is ['artifact', projectId, type], so this refetches
  // exactly the five and nothing else. Stable identity, so passing it as a prop does not churn.
  const retry = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["artifact", projectId] });
  }, [queryClient, projectId]);

  const statuses: Record<string, string> = {};
  ARTIFACT_TYPES.forEach((type, i) => {
    // data is null when the artifact_edits row doesn't exist yet (404 → null in useArtifact).
    const data = queries[i].data;
    if (data?.status) statuses[type] = data.status;
  });

  // POSITIVE assertion: every query has data. NOT `!some(isPending)` — that reports `true` for an
  // ERRORED query (isPending false, data undefined), which is the byte-identical signature of "no
  // artifacts exist". NOT `!isPending && !isError` either: equivalent today, but it enumerates the
  // failures we thought of instead of the success we require.
  //
  // `anyPending` is the SECOND test, not `anyErrored`: pending must win over errored so a mixed set
  // (some succeeded, one errored, one still in flight) reports 'loading' rather than flashing a
  // Retry button for a request that has not finished failing. 'error' is then the FALL-THROUGH —
  // reached only when every query has settled and at least one settled as a failure — which also
  // makes the branch total against any future TanStack state (isPaused, offline).
  const allSucceeded = queries.every((q) => q.isSuccess);
  const anyPending = queries.some((q) => q.isPending);

  const artifacts: ArtifactsState = allSucceeded
    ? {
        phase: "ready",
        // Artifacts that actually EXIST as artifact_edits rows — independent of whether the most
        // recent generation JOB succeeded. A failed regeneration does not un-generate the previous
        // run's output, and a chat-accepted edit never touches phase0_generations at all.
        //
        // Computed INSIDE the hook, in the 'ready' branch only. It is deliberately NOT a separate
        // variable at the call site, because a separate variable is something a consumer can read
        // without checking the phase — which is exactly H-2 and H-3.
        existing: ARTIFACT_TYPES.filter((t) => statuses[t] !== undefined),
      }
    : anyPending
      ? { phase: "loading" }
      : { phase: "error", retry };

  return { statuses, artifacts };
}

export function Phase0Page({ projectId }: Phase0PageProps): JSX.Element {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Shares ['project', id] with ProjectDetailPage, so this costs no extra request there.
  const { data: project } = useProject(projectId) as {
    data: Project | undefined;
  };
  // Member-only server-side (403 NOT_PROJECT_MEMBER); leadership/admin bypass. False while the
  // project is still loading so write controls never flash on for a non-member.
  const canManage = canActAsMember(user?.role, WRITE_ROLES, project);
  // The role would allow it but the viewer is not on the team — explain why controls are missing.
  const showNotMemberNotice =
    WRITE_ROLES.includes(user?.role ?? "") &&
    project !== undefined &&
    !canManage;

  const { openTabs, activeTab, openTab, activateTab, closeTab } = useFileTabs();
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  // T-40 — the 409 INPUT_CUSTOMER_MISMATCH payload while its dialog is open (null = closed).
  const [mismatch, setMismatch] = useState<InputCustomerMismatchDetails | null>(
    null,
  );
  const [removedMismatchFiles, setRemovedMismatchFiles] = useState<string[]>(
    [],
  );
  // The options of the run that hit the mismatch, so "Generate anyway" keeps e.g. a leadership
  // `force` choice made in the ConflictDialog.
  const lastGenerateOptions = useRef<GenerateOptions>({});

  // Focus plumbing. `closeTab` reports where focus must go; these tokens carry that instruction to
  // whichever child owns the target element. A monotonic token rather than a boolean so two
  // consecutive closes onto the same target still fire.
  const [tabFocus, setTabFocus] = useState<{
    token: number;
    tabId?: FileTabId;
  }>({ token: 0 });
  const [composerFocusToken, setComposerFocusToken] = useState(0);
  const focusSeq = useRef(0);

  const { statuses: artifactStatuses, artifacts } =
    useArtifactStatuses(projectId);

  /**
   * SOW-inference notice dismissal (§2.6 AD-4a).
   *
   * Client-side only and deliberately not written back: the inference is a property of the run that
   * produced the artifacts the user is currently reading, so it should stay recoverable for as long
   * as those artifacts are the current ones. It is per-run for free — every generation INSERTs a new
   * `phase0_generations` row, so the column starts NULL on each attempt with no reset logic.
   */
  const [sowNoticeDismissed, setSowNoticeDismissed] = useState(false);

  const query = useQuery({
    queryKey: ["phase0-status", projectId],
    queryFn: async (): Promise<Phase0StatusResponse> => {
      const response = await client.get<Phase0StatusResponse>(
        `/api/projects/${projectId}/phase0/status`,
      );
      return response.data;
    },
    enabled: Boolean(projectId),
    // Poll every 10s while a generation job is in flight (FR-MVP-029).
    // 'pending' counts as in flight too: the auto-trigger and the manual path both sit in
    // 'pending' during extraction, and a stop (Stop button / deleting a source) can land then.
    // Also every 10s while a SharePoint link download is pending/syncing.
    refetchInterval: (q) =>
      phase0StatusRefetchInterval(
        q.state.data?.generation?.status,
        q.state.data?.docs_sync_status,
      ),
  });

  const generate = useMutation({
    // `boolean` is the original `force` argument (ConflictDialog); the object form adds the T-40
    // `override_customer_check` flag, which is a SEPARATE server flag from `force` by design.
    mutationFn: async (
      opts?: boolean | GenerateOptions,
    ): Promise<GenerateResponse> => {
      const { force, overrideCustomerCheck } = normaliseGenerateOptions(opts);
      lastGenerateOptions.current = { force, overrideCustomerCheck };
      const response = await client.post<GenerateResponse>(
        `/api/projects/${projectId}/phase0/generate`,
        {
          ...(force ? { force: true } : {}),
          ...(overrideCustomerCheck ? { override_customer_check: true } : {}),
        },
        // T-40: input extraction + the customer check (≤8s) run before the 409/202 answer; give it
        // API Gateway's full 29s instead of the client's 10s default so the mismatch dialog shows.
        { timeout: 29_000 },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setMismatch(null);
      if (data?.status === "pending" || data?.status === "generating") {
        // 202: the run continues in the background. Show it as in flight straight away (this also
        // hides the Generate prompt), then let the status fetch and the WebSocket fill in the rest.
        queryClient.setQueryData<Phase0StatusResponse>(
          ["phase0-status", projectId],
          (old) =>
            old
              ? {
                  ...old,
                  generation: {
                    ...(old.generation ?? emptyGeneration(projectId)),
                    id: data.id,
                    status: data.status,
                    error_message: null,
                    completed_at: null,
                    sow_inferred_filename: null,
                  },
                }
              : old,
        );
        toast.success(
          "Generation started. This page updates when the artifacts are ready.",
        );
      } else {
        toast.success("Phase 0 artifacts generated");
      }
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
    onError: (err) => {
      // 409 INPUT_CUSTOMER_MISMATCH (T-40) — the server found inputs that look like another
      // customer and did not start. The dialog lists them with Remove / Generate anyway. The global
      // interceptor already toasts the server message, so no second toast here.
      const mismatchBody = (
        err as AxiosError<{
          code?: string;
          details?: InputCustomerMismatchDetails;
        }>
      )?.response?.data;
      if (mismatchBody?.code === "INPUT_CUSTOMER_MISMATCH") {
        setRemovedMismatchFiles([]);
        setMismatch(mismatchBody.details ?? { expected: "", files: [] });
        void queryClient.invalidateQueries({
          queryKey: ["phase0-status", projectId],
        });
        return;
      }
      // 409 GENERATION_IN_PROGRESS — a run for this project is already going (another click, another
      // user, or the upload auto-trigger). Not a fault: show it and pick up that run's status.
      if (
        (err as AxiosError<{ code?: string }>)?.response?.data?.code ===
        "GENERATION_IN_PROGRESS"
      ) {
        toast.info("Phase 0 generation is already running for this project.");
        void queryClient.invalidateQueries({
          queryKey: ["phase0-status", projectId],
        });
        return;
      }
      // 409 GENERATION_STOPPED — the run was stopped (Stop button, or a source file deleted) before
      // its result landed. The user caused that, so it is reported as information, not a fault.
      if (
        (err as AxiosError<{ code?: string }>)?.response?.data?.code ===
        "GENERATION_STOPPED"
      ) {
        toast.info(
          errorMessage(err, "Generation was stopped before it finished."),
        );
        void queryClient.invalidateQueries({
          queryKey: ["phase0-status", projectId],
        });
        return;
      }
      if (toastIfNotProjectMember(err)) return;
      toast.error(errorMessage(err, "Failed to generate artifacts"));
      // §2.7 — on UNREADABLE_SOURCE_DOCUMENTS the next action is always "look at what I uploaded",
      // so put the user there rather than leaving them to find the panel. This is also what makes
      // the §3.2.1 L-8 edge case (state D offered with zero sources) self-correct in one click: the
      // 422 arrives within seconds and lands them in Sources, where state A would have sent them.
      const code = (err as AxiosError<{ code?: string }>)?.response?.data?.code;
      if (code === "UNREADABLE_SOURCE_DOCUMENTS") setSourcesOpen(true);
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
  });

  // T-40 "Remove file" — the same DELETE /phase0/files/{filename} action SourcesSheet uses.
  const removeMismatchFile = useMutation({
    mutationFn: async (filename: string): Promise<string> => {
      await client.delete(
        `/api/projects/${projectId}/phase0/files/${encodeURIComponent(filename)}`,
      );
      return filename;
    },
    onSuccess: (filename) => {
      setRemovedMismatchFiles((prev) => [...prev, filename]);
      toast.success(`Removed ${filename}`);
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
    onError: (err) => {
      if (!toastIfNotProjectMember(err))
        toast.error(errorMessage(err, "Failed to remove the file"));
    },
  });

  const stop = useMutation({
    mutationFn: async (): Promise<void> => {
      await client.post(`/api/projects/${projectId}/phase0/stop`);
    },
    onSuccess: () => {
      toast.success("Generation stopped");
      void queryClient.invalidateQueries({
        queryKey: ["phase0-status", projectId],
      });
    },
    onError: (err) => {
      if (!toastIfNotProjectMember(err))
        toast.error(errorMessage(err, "Failed to stop generation"));
    },
  });

  // ── Item 6 (§7.2, AD-14) — measured workspace height ─────────────────────────────────────────
  //
  // THE DEFECT: the workspace carried `min-h-[620px]`, a floor with no ceiling. Workspace + page
  // header + banners + `ProjectDetailPage`'s sticky header (which is `sticky`, so it still occupies
  // flow space) exceeds the viewport on any laptop display, so `AppShell`'s `<main>` scrolls — and
  // the user must scroll the PAGE to reach the composer while the transcript scrolls separately
  // INSIDE it. Two nested scrollbars, which is the reported symptom.
  //
  // Nothing inside the workspace needs changing: `ChatTranscript` already has
  // `min-h-0 flex-1 overflow-y-auto`, and `FileContentView`'s panel has the same. Only the
  // workspace's OUTER height was wrong.
  //
  // MEASURED rather than a hardcoded `calc(100dvh - Nrem)` (AD-14): the chrome above is genuinely
  // variable — the sticky header's height changes with title wrapping and the PhaseStepper's
  // responsive layout, and this page renders between zero and three banners plus a system prompt.
  // Any fixed subtrahend is wrong for some combination, and wrong in the direction that
  // reintroduces page scroll.
  //
  // STILL MEASURED after the compact-chrome change. `ProjectDetailPage` now swaps to a ~68px
  // chrome (one identity line + the tab bar) while this tab is active instead of the ~230px
  // breadcrumb/title/PhaseStepper stack, which is where the transcript's extra height came from.
  // That makes the subtrahend SMALLER, not FIXED: the identity line still wraps on narrow
  // viewports, the tab bar still wraps, and the banners above this workspace are unchanged.
  // Hardcoding 68 would be wrong on exactly the viewports where it matters most.
  const workspaceRef = useRef<HTMLDivElement>(null);
  const [workspaceHeight, setWorkspaceHeight] = useState<number | null>(null);

  const generationStatus = query.data?.generation?.status;
  const hasErrorMessage = Boolean(query.data?.generation?.error_message);
  const measuredSourceCount = query.data?.uploaded_files.length ?? 0;

  // `useLayoutEffect`, not `useEffect`: measuring after paint causes a visible one-frame jump.
  useLayoutEffect(() => {
    const measure = (): void => {
      const el = workspaceRef.current;
      if (!el) return;
      // `window.innerHeight`, not `100dvh` in CSS: `getBoundingClientRect().top` is already in the
      // same coordinate space, and mixing a measured top with a CSS viewport unit can disagree by
      // the mobile browser-chrome delta.
      const top = el.getBoundingClientRect().top;
      // `--spacing-page` (globals.css) — <main>'s bottom padding, and <main>'s border box ends at
      // the viewport bottom (AppShell: `h-screen` + `overflow-hidden` + `flex-1` main). Was 24,
      // which is not the token's value: the workspace ran 8px past the gutter and that alone put a
      // scrollbar on <main>, which is half of the reported "scroll hides everything" symptom. The
      // other half was the outer project chrome, fixed in ProjectDetailPage.
      const GUTTER_PX = 32;
      const MIN_PX = 420; // floor on short viewports; the page may scroll, which is correct there
      setWorkspaceHeight(
        Math.max(window.innerHeight - top - GUTTER_PX, MIN_PX),
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // Re-measure when the chrome above changes height.
    //
    // `artifacts.phase` AND the count are BOTH listed deliberately (M-9). The three chrome heights
    // this effect exists to absorb are different renders at different heights:
    //     'loading'          → skeleton card
    //     'error'            → state E card (heading + body + Retry — taller than the skeleton)
    //     'ready', length 0  → state B card (the tallest — four sentences of explanation)
    // Reducing them to one numeric value would make 'loading' and 'error' identical and SKIP the
    // re-measure on the skeleton → state-B transition, which is the exact height change Item 6
    // exists for. The -1 sentinel never collides with a real count, because `existing.length` is
    // 0..5.
    //
    // A ResizeObserver on the chrome would be marginally more precise, but its callback also fires
    // on the workspace's own resize — a feedback loop. The dep array covers every element that can
    // change height on this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generationStatus,
    hasErrorMessage,
    measuredSourceCount,
    sowNoticeDismissed,
    artifacts.phase,
    artifacts.phase === "ready" ? artifacts.existing.length : -1,
  ]);

  const handleCloseTab = useCallback(
    (artifactType: ArtifactType): void => {
      const target = closeTab(artifactType);
      if (!target) return;
      focusSeq.current += 1;
      if (target.kind === "composer") {
        // The chat is now the visible surface; put the caret where the user can keep working.
        setComposerFocusToken(focusSeq.current);
      } else {
        setTabFocus({ token: focusSeq.current, tabId: target.tabId });
      }
    },
    [closeTab],
  );

  const handleOpenArtifact = useCallback(
    (artifactType: ArtifactType): void => {
      openTab(artifactType);
      // The Sources panel deliberately stays open — it is non-modal, and closing it on selection
      // would make it behave like the modal it replaced.
    },
    [openTab],
  );

  /**
   * ── INVARIANT: `hasLocked` is only ever read from a COMPLETE `statuses` map ─────────────────
   * A missing key reads as "not locked", so a partial map would under-report locks and let a
   * regenerate silently clobber a committed artifact. Every control that can reach
   * `handleRegenerateClick` — `ChatSystemPrompt` states C and D and the §3.2.2 line — is gated on
   * `artifacts.phase === 'ready'`, which requires all five keys to be resolved. The header's `Stop`
   * button is the only other remaining control and it does not read `statuses`.
   *
   * This is the one place the partial-map hazard could re-enter through a path that is not a
   * Generate button, so it is asserted by a test rather than only documented (§3.3.1, test 25q).
   */
  const hasLocked = Object.values(artifactStatuses).some((s) =>
    LOCKED_STATUSES.includes(s as ArtifactStatus),
  );

  const handleRegenerateClick = (): void => {
    if (hasLocked) setConflictOpen(true);
    else generate.mutate(false);
  };

  // ── Loading / error ──
  if (query.isLoading) {
    return (
      <div
        className="flex flex-col gap-3"
        role="status"
        aria-label="Loading Phase 0 status"
      >
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-2/3" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-outlines p-6">
        <p className="text-sm text-error">Failed to load Phase 0 status.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  const {
    generation,
    uploaded_files,
    latest_input_uploaded_at = null,
    docs_sync_status = null,
  } = query.data ?? {
    generation: null,
    uploaded_files: [],
    latest_input_uploaded_at: null,
    docs_sync_status: null,
  };
  // `generate.isPending` covers the few seconds before the 202 lands, so a second click cannot start
  // a second run.
  const isGenerating =
    generate.isPending ||
    generation?.status === "generating" ||
    generation?.status === "pending";

  // `hasGeneratedArtifacts` is DELETED (§4.2). It was `generation?.status === 'complete'` — a
  // statement about the latest JOB, not about whether artifacts exist. It gated SourcesSheet's
  // Generated-docs section, so a project whose latest run failed showed "Nothing generated yet"
  // while the previous run's Preliminary SRS opened as a tab and rendered content. Artifact
  // existence now comes from `artifacts` (the five per-artifact GETs). Item 2 removed the Generate
  // and Regenerate buttons that were its other two consumers, so the variable disappears entirely
  // rather than lingering with one caller.

  return (
    <div className="flex flex-col gap-4">
      {/* ── Page header: identity + the ONE remaining workspace-level action ──
          `Generate`, `Regenerate` and `Sources` have all left this header:
            • Generate / Regenerate  → `ChatSystemPrompt` in the transcript (Item 2). One entry
              point, in the surface that explains the ~10-minute cost.
            • Sources                → the chat composer's file-tag bar (Item 7). Spatially it
              belonged to the page; functionally it belongs to the conversation.
          `Stop` STAYS: it acts on an in-flight job, is time-critical, and must not require
          scrolling a transcript to reach. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          <h2 className="text-base font-semibold text-text-primary">
            Phase 0 Artifacts
          </h2>
          {generation && statusBadge(generation.status)}
        </div>

        <div className="flex items-center gap-2">
          {canManage && isGenerating && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => stop.mutate()}
              disabled={stop.isPending}
            >
              {stop.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <X className="h-4 w-4" aria-hidden="true" />
              )}
              Stop
            </Button>
          )}
        </div>
      </div>

      {showNotMemberNotice && (
        <p
          className="flex items-center gap-2 rounded-md border border-outlines bg-subtle-bg p-3 text-sm text-text-secondary"
          role="status"
        >
          <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
          Only members of this project&apos;s team can generate or edit
          artifacts.
        </p>
      )}

      {/* ── Generation status banners (informational — they no longer gate the workspace) ──

          Failure disclosure has ONE home at a time (L-6). When the chat is full-height (no file tab
          open) `ChatSystemPrompt` state D owns it — it carries the message AND the `Try again`
          action. This banner is the docked-mode stand-in, for when a file tab is open and no prompt
          renders at all. Rendering both printed `error_message` twice on the same screen.

          `activeTab !== CHAT_TAB` is the exact COMPLEMENT of the condition under which the prompt
          renders at `variant="full"`, so the message is never shown twice and never shown zero
          times — except during `artifacts.phase === 'loading'`, where the skeleton is showing and a
          sub-second gap in a non-actionable message is the correct trade for not flashing
          contradictory content. */}
      {generation?.status === "failed" && activeTab !== CHAT_TAB && (
        <div
          className="flex items-start gap-2 rounded-md border border-error bg-badge-red-bg p-3 text-sm text-error"
          role="alert"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{generation.error_message ?? "Generation failed."}</span>
        </div>
      )}

      {generation?.status === "generating" && (
        <p
          className="flex items-center gap-2 rounded-md border border-outlines bg-subtle-bg p-3 text-sm text-text-secondary"
          role="status"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Generating artifacts — this can take up to a few minutes.
        </p>
      )}

      {/* ── SOW-inference notice (§2.6 AD-4a) ──
          Arrives on the status POLL, seconds into a ten-minute run, while `Stop` is still live —
          which is what makes its advice actionable. Written only when more than one readable
          candidate existed, so a non-null value always means a choice was made that could have been
          wrong. */}
      {generation?.sow_inferred_filename && !sowNoticeDismissed && (
        <div
          className="flex items-start justify-between gap-3 rounded-md border border-outlines bg-badge-yellow-bg p-3 text-sm text-text-primary"
          role="status"
          aria-live="polite"
        >
          <p>
            <strong>
              Using <code>{generation.sow_inferred_filename}</code> as the
              statement of work.
            </strong>{" "}
            No uploaded file had &ldquo;SOW&rdquo; in its name, so the largest
            document was used. If that is wrong, stop the run, rename the file
            and generate again.
          </p>
          <Button
            variant="link"
            size="sm"
            className="h-auto shrink-0 p-0"
            onClick={() => setSowNoticeDismissed(true)}
          >
            Dismiss
          </Button>
        </div>
      )}

      {/* The static `!generation` hint paragraph is DELETED — `ChatSystemPrompt` state A supersedes
          it, and it pointed at a `Generate` button that no longer exists. */}

      {/* ── Workspace: tab strip + the active panel ── */}
      <div
        ref={workspaceRef}
        style={workspaceHeight ? { height: `${workspaceHeight}px` } : undefined}
        className="flex min-h-[420px] flex-col overflow-hidden rounded-lg border border-outlines bg-white"
      >
        <FileTabStrip
          openTabs={openTabs}
          activeTab={activeTab}
          onActivate={activateTab}
          onClose={handleCloseTab}
          focusToken={tabFocus.token}
          focusTabId={tabFocus.tabId}
        />

        {/* Chat panel — the DEFAULT view. Kept mounted and hidden rather than unmounted so the
            composer draft, scroll position and WebSocket subscription survive a trip into a file
            and back. Unmounting it would also drop and re-add the `ws-online` listener on every tab
            switch, which is churn for no benefit. */}
        <div
          role="tabpanel"
          id={`file-tabpanel-${CHAT_TAB}`}
          aria-labelledby={`file-tab-${CHAT_TAB}`}
          hidden={activeTab !== CHAT_TAB}
          className={
            activeTab === CHAT_TAB ? "flex min-h-0 flex-1 flex-col" : "hidden"
          }
        >
          <ProjectChatView
            projectId={projectId}
            variant="full"
            focusToken={composerFocusToken}
            onOpenArtifact={handleOpenArtifact}
            // Item 7 — the Sources button now lives in the composer's file-tag bar.
            onOpenSources={() => setSourcesOpen((o) => !o)}
            sourcesCount={uploaded_files.length}
            sourcesExpanded={sourcesOpen}
            /**
             * Item 3 / AD-8 — chip availability. BOTH unresolved phases map to `undefined`, which
             * means "not yet known → enable all five". That is deliberately the OPPOSITE of
             * `ChatSystemPrompt` and `SourcesSheet`, which fail CLOSED.
             *
             * The asymmetry is justified by the asymmetry in blast radius: a wrong chip costs a
             * recoverable `404 ARTIFACT_NOT_FOUND` toast from `postTurn`'s backstop, whereas a
             * wrong Generate button costs the user's drafts. Disabled-then-enabled is also a flash
             * that can swallow a click the user already aimed at an enabled-looking chip.
             *
             * `undefined` ("don't know") is distinct from `[]` ("none exist → disable all").
             */
            availableArtifacts={
              artifacts.phase === "ready" ? artifacts.existing : undefined
            }
            // Item 2 — the generation prompt's inputs.
            artifacts={artifacts}
            generation={generation}
            latestInputUploadedAt={latest_input_uploaded_at}
            canManage={canManage}
            canWrite={canManage}
            isGenerating={Boolean(isGenerating)}
            onGenerate={() => generate.mutate(false)}
            onRegenerate={handleRegenerateClick}
          />
        </div>

        {/* File panels — mounted only while active, so each artifact fetches on first open and a
            closed tab's queries stop being observed. */}
        {openTabs.map(
          (artifactType) =>
            activeTab === artifactType && (
              <div
                key={artifactType}
                role="tabpanel"
                id={`file-tabpanel-${artifactType}`}
                aria-labelledby={`file-tab-${artifactType}`}
                className="min-h-0 flex-1 overflow-y-auto p-4"
              >
                <FileContentView
                  projectId={projectId}
                  artifactType={artifactType}
                  userRole={user?.role ?? ""}
                  userId={user?.userId ?? ""}
                  canManage={canManage}
                />
              </div>
            ),
        )}
      </div>

      <SourcesSheet
        open={sourcesOpen}
        onOpenChange={setSourcesOpen}
        projectId={projectId}
        uploadedFiles={uploaded_files}
        canManage={canManage}
        docsSyncStatus={docs_sync_status}
        artifacts={artifacts}
        artifactStatuses={artifactStatuses}
        onOpenArtifact={handleOpenArtifact}
        openArtifacts={openTabs}
      />

      <ConflictDialog
        open={conflictOpen}
        artifactStatuses={artifactStatuses}
        userRole={user?.role ?? ""}
        onRegenerateSkipLocked={() => {
          setConflictOpen(false);
          generate.mutate(false);
        }}
        onForceOverwrite={() => {
          setConflictOpen(false);
          generate.mutate(true);
        }}
        onClose={() => setConflictOpen(false)}
      />

      <CustomerMismatchDialog
        open={mismatch !== null}
        details={mismatch}
        canManage={canManage}
        removingFile={
          removeMismatchFile.isPending
            ? (removeMismatchFile.variables ?? null)
            : null
        }
        removedFiles={removedMismatchFiles}
        onRemoveFile={(filename) => removeMismatchFile.mutate(filename)}
        onGenerateAnyway={() => {
          setMismatch(null);
          generate.mutate({
            ...lastGenerateOptions.current,
            overrideCustomerCheck: true,
          });
        }}
        onGenerateAgain={() => {
          setMismatch(null);
          generate.mutate({
            ...lastGenerateOptions.current,
            overrideCustomerCheck: false,
          });
        }}
        onClose={() => setMismatch(null)}
      />
    </div>
  );
}

export default Phase0Page;
